"use client"
import { addItemToCart } from "@/actions/selection/add-item-to-selection"
import { deleteSelectionOrderLine } from "@/actions/selection/delete-orderline-selection"
import { KeepSelection, keepSelection } from "@/actions/selection/keep-selection"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconBin from "@/app/(site)/_components/Atoms/Icons/IconBin"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import { Err, Ok } from "@/types/generic/Result"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Image from "next/image"
import { useEffect, useState } from "react"
import { match } from "ts-pattern"
import Heading from "../../Atoms/Heading"
import Text from "../../Atoms/Text"
import { BulletList } from "../../Molecules/BulletList"
import ButtonPill from "../../Molecules/ButtonPill"

import { reduceSelectionQuantity } from "@/actions/selection/reduce-selection"
import { removeVoucher } from "@/actions/selection/remove-voucher"
import formatCurrency from "@/app/(site)/_utils/_hooks/priceFormatter"
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"
import { getLangCode, getMarketCodeFromCentraLocale } from "@/app/(site)/_utils/misc"
import { transformDepictCarousel } from "@/lib/depict-client/helpers/transformDepictCarouselData"
import logger from "@/lib/logger/logger-init"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { addToCartDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { addToCartEvent, beginCheckoutEvent } from "@/lib/utils/tracking/gtmEvents"
import { t } from "@/translations"
import { SiteConfig } from "@/types"
import placeholder from "@assets/images/placeholder.png"
import toast from "react-hot-toast"
import { Skeleton } from "../../Atoms/Skeleton"
import ButtonLink from "../../Molecules/ButtonLink"
import DynamicLink from "../../Molecules/Link"
import DepictCarousel from "../../SanityBlocks/DepictCarousel"
import AddToCartModal from "../ProductDetail/Modals/AddToCartModal"
import AccordionShipping from "../ProductDetail/ProductAccordion/AccordionShipping"

export interface ReduceSelectionQuantity extends FormData {
    append(name: "item", value: string | Blob, fileName?: string): void
}

export interface AddSelectionQuantity extends FormData {
    append(name: "item", value: string | Blob, fileName?: string): void
}

export interface DeleteSelectionOrderLine extends FormData {
    append(name: "line", value: string | Blob, fileName?: string): void
}

type Props = {
    config: SiteConfig | undefined
    params: { slug: string; locale: string }
}

const CartModule: React.FC<Props> = ({ config, params }) => {
    const marketCode = getMarketCodeFromCentraLocale(params.locale)
    const langCode = getLangCode(params.locale) || ""
    const queryClient = useQueryClient()
    const { functionalConsent } = useOneTrustOptInLoad()
    const queryKey = "selection"

    // Fetch cart data
    const {
        data,
        refetch,
        error: useQueryError,
        isLoading,
        isStale,
        failureReason,
        fetchStatus,
        isFetchedAfterMount,
        isPaused
    } = useQuery({
        queryKey: [queryKey],
        networkMode: "always",
        queryFn: () => keepSelection(),
        staleTime: 60 * 1000,
        retry: 3,
        // structuralSharing: false, // activate if networkMode don't fix the stale issue
        refetchOnReconnect: "always",
        refetchOnWindowFocus: false,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
    })

    const [cart, setCart] = useState<KeepSelection>({})

    if (isStale && fetchStatus === "fetching" && !isFetchedAfterMount) {
        logger.info("Cart data is stale", {
            failureReason: failureReason,
            isStale: isStale,
            fetchStatus: fetchStatus,
            isFetchedAfterMount: isFetchedAfterMount,
            isPaused: isPaused,
            isLoading: isLoading
        })
    }

    const cartPageDepictCarousel = config?.depictSettings?.cartPageDepictCarousel

    useEffect(() => {
        if (data) {
            match(data)
                .with({ _tag: "success" }, ({ data }) => {
                    setCart(data)
                })
                .with({ _tag: "failure" }, ({ error }) => {
                    toast.error(
                        t(
                            langCode,
                            "An error occurred while loading cart data. Please try again."
                        )
                    )
                    logger.error("Error loading cart data", {
                        "with:failure": error,
                        data: data,
                        "Query Error": useQueryError
                    })
                    return null
                })
                .otherwise(() => {
                    toast.error(
                        t(langCode, "Unexpected error occurred. Please try again.")
                    )
                    logger.error("Unexpected error loading cart data", {
                        data: data,
                        "Query Error": useQueryError
                    })
                    return null
                })
        }
    }, [isLoading, data])

    if (
        Array.isArray(cartPageDepictCarousel) &&
        cartPageDepictCarousel.length > 0 &&
        cartPageDepictCarousel[0]?.useProductID
    ) {
        const cartCarousel = cartPageDepictCarousel[0]
        if (cartCarousel) {
            transformDepictCarousel(cartCarousel, cart, "cartPage")
        }
    }
    const emptyProductGroups =
        Array.isArray(cartPageDepictCarousel) &&
        cartPageDepictCarousel.length > 0 &&
        cartPageDepictCarousel.every(
            carousel =>
                Array.isArray(carousel.items) &&
                carousel.items.every(
                    item => !!item.depictProductDisplayId // Ensure depictProductDisplayId exists
                )
        )

    const handleRetry = () => {
        refetch()
    }

    // Match selection
    const selection = cart?.checkoutApiSelection?.selection

    // Check if cart is empty
    const isCartEmpty = selection?.items?.length === 0

    // Get price without tax and shipping
    const grandTotalPriceAsNumber = selection?.totals?.grandTotalPriceAsNumber as number
    const grandTotalPriceTaxAsNumber = selection?.totals
        ?.grandTotalPriceTaxAsNumber as number
    const shippingPriceAsNumber = selection?.totals?.shippingPriceAsNumber as number

    const shippingCountry = cart?.checkoutApiSelection?.location?.country

    const priceWithoutTaxAndShipping =
        grandTotalPriceAsNumber - (grandTotalPriceTaxAsNumber + shippingPriceAsNumber)

    const priceWithoutShipping = grandTotalPriceAsNumber - shippingPriceAsNumber

    // Get item amount
    const ItemAmount = selection?.items
        .map(item => item?.quantity)
        ?.reduce((a, b) => a + b, 0)

    // Get total price
    const itemsTotalPrice = selection?.totals?.itemsTotalPrice

    // Get vouchers
    const vouchers = selection?.discounts?.vouchers || []

    // Check if voucher is applied
    const isVoucherApplied = vouchers?.length > 0

    const handleItemAdd = async (item: any) => {
        const previousCart = queryClient.getQueryData([queryKey])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData([queryKey], updatedCart)
        try {
            const formData = new FormData()
            formData.append("item", item?.item)
            await addItemToCart(formData)
            addToCartEvent(item?.product, item)
            if (functionalConsent) {
                addToCartDepictEvent(item?.product, item)
            }
        } catch (error) {
            queryClient.setQueryData([queryKey], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: [queryKey], refetchType: "all" })
        }
    }

    const handleItemDecrease = async (lineItem: string) => {
        const previousCart = queryClient.getQueryData([queryKey])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData([queryKey], updatedCart)

        try {
            const formData: ReduceSelectionQuantity =
                new FormData() as ReduceSelectionQuantity
            formData.append("item", lineItem)
            await reduceSelectionQuantity(formData)
        } catch (error) {
            queryClient.setQueryData([queryKey], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: [queryKey] })
        }
    }
    const handleOrderLineDelete = async (lineId: string) => {
        const previousCart = queryClient.getQueryData([queryKey])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData([queryKey], updatedCart)

        try {
            await deleteSelectionOrderLine(lineId)
        } catch (error) {
            queryClient.setQueryData([queryKey], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: [queryKey], refetchType: "all" })
        }
    }

    const handleVoucherDelete = async (voucher: string) => {
        const res = await removeVoucher({ voucher: voucher })

        match(res)
            .with(Ok, () => {
                queryClient.invalidateQueries({
                    queryKey: [queryKey]
                })
            })
            .with(Err, () => {
                toast.error(
                    t(
                        langCode,
                        "An error occurred while removing the voucher. Please try again."
                    )
                )
            })
            .exhaustive()
    }

    if (useQueryError && !isLoading) {
        return (
            <div className="flex h-screen flex-col items-center justify-center py-24">
                <Heading type="h2">{t(langCode, "Something went wrong")}</Heading>
                <p className="text-main-dark-grey">
                    {t(langCode, "We were unable to load your cart. Please try again.")}
                </p>
                <ButtonPill
                    color="black"
                    variant="primary"
                    size="large"
                    onClick={handleRetry}
                    className="mt-16"
                >
                    {t(langCode, "Retry")}
                </ButtonPill>
            </div>
        )
    }

    if (isCartEmpty && !isLoading) {
        return (
            <div className="flex items-center justify-center">
                <div className="flex flex-col items-center justify-center p-200">
                    <Heading type="h2" className="pb-32">
                        {t(langCode, "Your cart is empty")}
                    </Heading>
                    <ButtonLink
                        href="/"
                        prefetch={false}
                        color="black"
                        variant="primary"
                        size="large"
                    >
                        {t(langCode, "Continue Shopping")}{" "}
                        <IconArrowRight aria-hidden="true" className="h-20 w-20" />
                    </ButtonLink>
                </div>
            </div>
        )
    }

    return (
        <>
            <AddToCartModal isCart={true} />

            <div>
                <div className="mb-148 mt-72 grid grid-cols-1 px-15 md:mt-0 md:grid-cols-6 md:gap-43 md:px-24">
                    <div className="col-span-4 mb-72 md:mb-0">
                        <div className="mb-32 md:mb-48">
                            <Heading type="h2">{t(langCode, "your cart")}</Heading>

                            <Heading type="h2" className="flex text-main-dark-grey">
                                {isLoading ? (
                                    <Skeleton className="h-28 w-30 !animate-pulse md:h-32 md:w-40" />
                                ) : (
                                    <>{ItemAmount}&nbsp;</>
                                )}
                                {t(langCode, "product")}
                                {ItemAmount && ItemAmount > 1 ? "s" : ""}
                            </Heading>
                        </div>
                        {isLoading ? (
                            <Skeleton className="h-150 w-full !animate-pulse md:h-130" />
                        ) : (
                            (selection?.items || [])?.map((item, index) => {
                                const lineId = item?.line
                                const imageSrc =
                                    item?.product?.media?.mini?.[0] || placeholder
                                const productName = item?.product?.name
                                const variantName = item?.product?.variantName
                                const productPrice = item?.product?.price
                                const productSize = item?.size
                                const priceBeforeDiscount =
                                    item?.product?.priceBeforeDiscount
                                const productQuantity = item?.quantity
                                const productLensName = item?.product?.lens?.lens_name
                                const productUri = item?.product?.uri

                                return (
                                    <div
                                        key={index}
                                        className="mr-0 grid grid-cols-6 gap-16 border-t border-black py-16 md:grid-cols-8"
                                    >
                                        <div className="col-span-2 md:col-span-1">
                                            <DynamicLink href={"/product/" + productUri}>
                                                <Image
                                                    className="bg-main-light-grey"
                                                    alt={productName}
                                                    src={imageSrc}
                                                    width={150}
                                                    height={150}
                                                />
                                            </DynamicLink>
                                        </div>
                                        <div className="justify col-span-2 flex flex-col">
                                            <div>
                                                <DynamicLink
                                                    href={"/product/" + productUri}
                                                >
                                                    <Text
                                                        size="extrasmall"
                                                        weight="regular"
                                                        className="!leading-160 mt-2 text-12 md:text-16"
                                                    >
                                                        {productName}
                                                    </Text>
                                                </DynamicLink>
                                                <Text
                                                    size="extrasmall"
                                                    weight="regular"
                                                    className="block text-main-dark-grey md:hidden"
                                                >
                                                    {variantName}
                                                </Text>
                                                <Text
                                                    size="extrasmall"
                                                    weight="regular"
                                                    className="text-main-dark-grey md:hidden"
                                                >
                                                    {productSize}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text
                                                    size="small"
                                                    weight="regular"
                                                    className="block text-main-dark-grey md:hidden"
                                                >
                                                    {productPrice}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className="col-span-2 hidden flex-col gap-8 md:flex">
                                            {/* If Mobile, hide this */}
                                            <div>
                                                <Text
                                                    size="medium"
                                                    weight="regular"
                                                    className="text-main-dark-grey"
                                                >
                                                    {variantName}
                                                </Text>
                                                <Text
                                                    size="medium"
                                                    weight="regular"
                                                    className="text-main-dark-grey"
                                                >
                                                    {productLensName}
                                                </Text>
                                            </div>
                                            {productSize === "One Size" ? (
                                                <></>
                                            ) : (
                                                <>
                                                    <Text
                                                        size="medium"
                                                        weight="regular"
                                                        className="text-main-dark-grey"
                                                    >
                                                        {productSize}
                                                    </Text>
                                                </>
                                            )}
                                        </div>
                                        <div className="col-span-1 hidden md:block">
                                            <div className="flex items-center gap-4 md:gap-8">
                                                <div
                                                    onClick={() =>
                                                        handleItemDecrease(lineId)
                                                    }
                                                    className="flex cursor-pointer items-center"
                                                    role="button"
                                                    aria-label="Decrease quantity"
                                                >
                                                    <IconMinus className="h-18 w-18" />
                                                </div>
                                                <Text
                                                    size="medium"
                                                    weight="regular"
                                                    className="text-main-dark-grey"
                                                >
                                                    {item?.quantity}
                                                </Text>
                                                <div
                                                    onClick={() => handleItemAdd(item)}
                                                    className="flex cursor-pointer items-center"
                                                    role="button"
                                                    aria-label="Increase quantity"
                                                >
                                                    <IconPlus className="h-18 w-18" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-1 hidden md:block">
                                            {item?.product?.showAsOnSale ? (
                                                <>
                                                    <Text
                                                        size="medium"
                                                        weight="regular"
                                                        className="text-main-dark-grey"
                                                    >
                                                        {productPrice}
                                                    </Text>
                                                    <Text
                                                        size="medium"
                                                        weight="regular"
                                                        className="text-main-dark-grey line-through"
                                                    >
                                                        {priceBeforeDiscount}
                                                    </Text>
                                                </>
                                            ) : (
                                                <Text
                                                    size="medium"
                                                    weight="regular"
                                                    className="text-main-dark-grey"
                                                >
                                                    {productPrice}
                                                </Text>
                                            )}
                                        </div>
                                        <div className="col-span-2 flex justify-end md:col-span-1">
                                            <div className="flex flex-col justify-between">
                                                <div
                                                    className="flex cursor-pointer justify-end p-12 md:p-0"
                                                    onClick={() =>
                                                        handleOrderLineDelete(lineId)
                                                    }
                                                    role="button"
                                                    aria-label="Remove item from cart"
                                                >
                                                    <IconBin className="h-18 w-18 md:h-24 md:w-24" />
                                                </div>
                                                <div className="flex items-end justify-end md:hidden">
                                                    <div
                                                        onClick={() =>
                                                            handleItemDecrease(lineId)
                                                        }
                                                        className="cursor-pointer p-12"
                                                        role="button"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <IconMinus className="h-18 w-18" />
                                                    </div>
                                                    <Text
                                                        size="medium"
                                                        weight="regular"
                                                        className="py-12 text-main-dark-grey"
                                                    >
                                                        {productQuantity}
                                                    </Text>
                                                    <div
                                                        onClick={() =>
                                                            handleItemAdd(item)
                                                        }
                                                        className="cursor-pointer p-12"
                                                        role="button"
                                                        aria-label="Increase quantity"
                                                    >
                                                        <IconPlus className="h-18 w-18" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                    <div className="col-span-2">
                        <div className="mb-32 text-main-dark-grey md:mb-48">
                            <Heading type="h2" className="text-black">
                                {t(langCode, "Order Summary")}
                            </Heading>

                            {isLoading ? (
                                <Skeleton className="h-24 w-5/12 !animate-pulse md:h-32 md:w-1/3" />
                            ) : (
                                <Heading type="h2" className="text-main-dark-grey">
                                    {formatCurrency({
                                        amount:
                                            shippingCountry === "CA"
                                                ? priceWithoutTaxAndShipping
                                                : priceWithoutShipping,
                                        locale: params.locale || "en-US",
                                        currency: selection?.currency || "USD"
                                    })}
                                </Heading>
                            )}
                        </div>

                        <div className="border-y border-black py-32 md:py-48">
                            <div className="flex flex-col flex-wrap">
                                <div className="mb-16 flex justify-between text-main-dark-grey md:mb-24">
                                    <Text size="medium" weight="regular">
                                        {t(langCode, "Products")}
                                    </Text>
                                    {isLoading ? (
                                        <Skeleton className="h-22 w-1/3 !animate-pulse md:h-32" />
                                    ) : (
                                        <Text size="medium" weight="regular">
                                            {itemsTotalPrice}
                                        </Text>
                                    )}
                                </div>
                                {isVoucherApplied &&
                                    vouchers.map((voucher, index) => {
                                        if (
                                            voucher?.voucher ===
                                            FREE_SHIPPING_VOUCHER_CODE
                                        )
                                            return null
                                        return (
                                            <div
                                                key={index}
                                                className="mb-16 flex justify-between gap-10 md:mb-24"
                                            >
                                                <div className="flex gap-8">
                                                    <Text
                                                        size="medium"
                                                        weight="regular"
                                                        className="text-main-dark-grey"
                                                    >
                                                        {t(langCode, "Voucher code")}{" "}
                                                        &quot;
                                                        {voucher?.voucher}
                                                        &quot;
                                                    </Text>
                                                    <button
                                                        onClick={() =>
                                                            handleVoucherDelete(
                                                                voucher.voucher
                                                            )
                                                        }
                                                    >
                                                        <IconClose className="h-16 w-16" />
                                                    </button>
                                                </div>
                                                <Text
                                                    size="medium"
                                                    weight="regular"
                                                    className="text-main-dark-grey"
                                                >
                                                    {voucher?.priceOff}
                                                </Text>
                                            </div>
                                        )
                                    })}
                            </div>

                            <div className="flex justify-between">
                                <Text
                                    size="medium"
                                    weight="regular"
                                    className="text-24 md:text-16"
                                >
                                    {t(langCode, "Total")}
                                </Text>
                                {isLoading ? (
                                    <Skeleton className="h-36 w-3/6 !animate-pulse md:h-32 md:w-1/3" />
                                ) : (
                                    <Text
                                        size="medium"
                                        weight="regular"
                                        className="text-24 md:text-16"
                                    >
                                        {formatCurrency({
                                            amount:
                                                shippingCountry === "CA"
                                                    ? priceWithoutTaxAndShipping
                                                    : priceWithoutShipping,
                                            locale: params.locale || "en-US",
                                            currency: selection?.currency || "USD"
                                        })}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <div className="pb-24 pt-32 md:pt-48">
                            <ButtonLink
                                href="/checkout"
                                onClick={e => {
                                    beginCheckoutEvent(cart)
                                }}
                                color="black"
                                variant="primary"
                                size="large"
                            >
                                {t(langCode, "Go to Checkout")}
                                <IconArrowRight
                                    aria-hidden="true"
                                    className="w- 20 h-20"
                                />
                            </ButtonLink>
                        </div>
                        <div className="mb-24 flex gap-10"></div>
                        <div>
                            {marketCode &&
                                config?.cartBulletList &&
                                config?.cartBulletList[marketCode] && (
                                    <div>
                                        <BulletList
                                            title={config.cartBulletList[marketCode].map(
                                                (item: { text: string }) => item.text
                                            )}
                                        />
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
                {emptyProductGroups && (
                    <DepictCarousel
                        hasQuickShop
                        isCartPage
                        params={params}
                        data={config?.depictSettings?.cartPageDepictCarousel?.[0]}
                    />
                )}
                <div className="mb-144 mt-72 px-24">
                    {config?.cartAccordion?.[marketCode]?.map((content, index) => (
                        <AccordionShipping key={index} content={content} />
                    ))}
                </div>
            </div>
        </>
    )
}

export default CartModule
